import {
	sanitizeRawApiErrorBodyForUser,
	userFriendlyMessageForHttpError,
} from "@Front/Utils/sanitizeApiErrorBodyForUser";

/**
 * Statuts pour lesquels le backend renvoie en général un `message` exploitable (JSON ou texte).
 * Couvre `useApiClient` (`status`) et `BaseApiService` / `LeCoffreApi` (`http_status` sur l’objet jeté).
 */
export const USER_FACING_API_CLIENT_ERROR_STATUSES: readonly number[] = [400, 403, 404, 409, 413];

function resolveHttpStatusFromThrownApiError(error: unknown): number | undefined {
	if (!error || typeof error !== "object") {
		return undefined;
	}
	const rec = error as Record<string, unknown>;
	const fromApiClient = rec["status"];
	if (typeof fromApiClient === "number") {
		return fromApiClient;
	}
	const fromHttp = rec["http_status"];
	if (typeof fromHttp === "number") {
		return fromHttp;
	}
	const resp = rec["response"];
	if (resp !== null && typeof resp === "object") {
		const rs = (resp as { status?: unknown }).status;
		if (typeof rs === "number") {
			return rs;
		}
	}
	return undefined;
}

function readThrownErrorMessage(error: unknown): string {
	if (typeof error === "string") {
		return error.trim();
	}
	if (error instanceof Error) {
		return error.message.trim();
	}
	if (error !== null && typeof error === "object" && "message" in error) {
		const m = (error as { message: unknown }).message;
		if (typeof m === "string") {
			return m.trim();
		}
	}
	return "";
}

/**
 * Retourne le message serveur lorsque l’erreur porte un statut HTTP dans `clientStatuses` et un texte non vide
 * (`useApiClient`, `LeCoffreApi` / `BaseApiService`, formes axios-like `response.status`) ; sinon `fallback`.
 */
export function userFacingMessageFromApiClientError(
	error: unknown,
	fallback: string,
	clientStatuses: readonly number[] = USER_FACING_API_CLIENT_ERROR_STATUSES,
): string {
	const status = resolveHttpStatusFromThrownApiError(error);
	const text = readThrownErrorMessage(error);
	const fromHttpMapper = userFriendlyMessageForHttpError(status, text.length > 0 ? text : null);
	if (fromHttpMapper) {
		return fromHttpMapper;
	}
	if (typeof status !== "number" || !clientStatuses.includes(status)) {
		return fallback;
	}
	if (text.length > 0) {
		return sanitizeRawApiErrorBodyForUser(text);
	}
	return fallback;
}

/**
 * Chaîne exploitable pour logs ou tests (`includes`) : `message` sur l’objet jeté par `BaseApiService`, sinon `Error.message`, sinon `String(error)`.
 */
export function coalesceThrownApiErrorText(error: unknown): string {
	const m = readThrownErrorMessage(error);
	if (m.length > 0) {
		return m;
	}
	if (error instanceof Error) {
		return error.message;
	}
	return String(error);
}
